import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, CalendarDays, X, Check, Loader2 } from 'lucide-react';
import { fetchSchedule, saveSchedule } from '../api';

function uid() {
  return (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
    'd-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const DayBuilder = ({ sid, savedItems }) => {
  const [days, setDays] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState('idle'); // idle | saving | saved
  const skipSave = useRef(true);
  const timer = useRef(null);

  // load existing schedule
  useEffect(() => {
    let active = true;
    fetchSchedule(sid)
      .then((d) => { if (active) setDays(d.days || []); })
      .catch(() => {})
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [sid]);

  // autosave (debounced) after first load
  useEffect(() => {
    if (!loaded) return;
    if (skipSave.current) { skipSave.current = false; return; }
    setSaving('saving');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      saveSchedule(sid, days)
        .then(() => setSaving('saved'))
        .catch(() => setSaving('idle'));
    }, 700);
    return () => clearTimeout(timer.current);
  }, [days, loaded, sid]);

  const addDay = () =>
    setDays((p) => [...p, { id: uid(), title: `Day ${p.length + 1}`, note: '', items: [] }]);

  const updateDay = (id, patch) =>
    setDays((p) => p.map((d) => (d.id === id ? { ...d, ...patch } : d)));

  const removeDay = (id) => setDays((p) => p.filter((d) => d.id !== id));

  const moveDay = (idx, dir) =>
    setDays((p) => {
      const next = [...p];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return p;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const addItemToDay = (dayId, refId) => {
    const src = savedItems.find((s) => s.ref_id === refId);
    if (!src) return;
    setDays((p) =>
      p.map((d) =>
        d.id === dayId
          ? d.items.some((i) => i.ref_id === refId)
            ? d
            : { ...d, items: [...d.items, { ref_id: src.ref_id, title: src.title, subtitle: src.subtitle, to: src.to, type: src.type }] }
          : d
      )
    );
  };

  const removeItemFromDay = (dayId, refId) =>
    setDays((p) => p.map((d) => (d.id === dayId ? { ...d, items: d.items.filter((i) => i.ref_id !== refId) } : d)));

  if (!loaded) {
    return <div className="py-10 flex justify-center text-[#92722A]"><Loader2 className="w-7 h-7 animate-spin" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#e0564e]" />
          <h2 className="font-display text-[26px] font-semibold text-[#1B1D21]">Day-by-day itinerary</h2>
        </div>
        <div className="flex items-center gap-3">
          {saving === 'saving' && <span className="text-[13px] text-[#8a7a63] inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</span>}
          {saving === 'saved' && <span className="text-[13px] text-[#2F663C] inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Saved</span>}
          <button onClick={addDay} className="btn-sunset inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-semibold">
            <Plus className="w-4 h-4" /> Add a day
          </button>
        </div>
      </div>

      {days.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-[#D7BC6D] bg-[#EFE7D2] p-8 text-center">
          <p className="text-[16px] text-[#4B4F58]">
            Build a schedule from what you&apos;ve saved. Add a day, then drop in emirates and hotels with your own notes.
          </p>
          <button onClick={addDay} className="mt-5 btn-outline-ink inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-semibold">
            <Plus className="w-4 h-4" /> Add your first day
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          {days.map((d, idx) => (
            <div key={d.id} className="rounded-2xl bg-white border border-[#E6D7A2] shadow-[0_14px_34px_-28px_rgba(27,29,33,0.45)] overflow-hidden">
              <div className="card-rainbow-top h-[5px] w-full" />
              <div className="p-6">
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-9 h-9 rounded-full sunset-gradient text-white text-[14px] font-bold shrink-0">{idx + 1}</span>
                  <input
                    value={d.title}
                    onChange={(e) => updateDay(d.id, { title: e.target.value })}
                    className="flex-1 bg-transparent font-display text-[20px] font-semibold text-[#1B1D21] outline-none border-b border-transparent focus:border-[#E6D7A2] py-1"
                    placeholder={`Day ${idx + 1}`}
                  />
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => moveDay(idx, -1)} disabled={idx === 0} aria-label="Move up" className="w-8 h-8 inline-flex items-center justify-center rounded-full text-[#9aa7b3] hover:text-[#1B1D21] hover:bg-[#F2ECCF] disabled:opacity-30 transition-colors"><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => moveDay(idx, 1)} disabled={idx === days.length - 1} aria-label="Move down" className="w-8 h-8 inline-flex items-center justify-center rounded-full text-[#9aa7b3] hover:text-[#1B1D21] hover:bg-[#F2ECCF] disabled:opacity-30 transition-colors"><ChevronDown className="w-4 h-4" /></button>
                    <button onClick={() => removeDay(d.id)} aria-label="Delete day" className="w-8 h-8 inline-flex items-center justify-center rounded-full text-[#9aa7b3] hover:text-white hover:bg-[#e0564e] transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                {/* assigned items */}
                {d.items.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {d.items.map((it) => (
                      <span key={it.ref_id} className="inline-flex items-center gap-1.5 rounded-full bg-[#F2ECCF] text-[#8a5a2e] pl-3 pr-1.5 py-1 text-[13px] font-medium">
                        {it.title}
                        <button onClick={() => removeItemFromDay(d.id, it.ref_id)} aria-label="Remove" className="w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-[#e0564e] hover:text-white transition-colors"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}

                {/* add item */}
                <div className="mt-4">
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value) addItemToDay(d.id, e.target.value); }}
                    className="rounded-full bg-[#fbefdf] border border-[#E6D7A2] text-[#45596b] text-[14px] font-medium px-4 py-2 outline-none focus:ring-2 focus:ring-[#e0564e]"
                  >
                    <option value="">+ Add a saved place…</option>
                    {savedItems.map((s) => (
                      <option key={s.ref_id} value={s.ref_id}>{s.title}</option>
                    ))}
                  </select>
                  {savedItems.length === 0 && (
                    <span className="ml-3 text-[13px] text-[#8a7a63]">Bookmark emirates or hotels first to add them here.</span>
                  )}
                </div>

                {/* notes */}
                <textarea
                  value={d.note}
                  onChange={(e) => updateDay(d.id, { note: e.target.value })}
                  rows={2}
                  placeholder="Notes for the day — bookings, times, reminders…"
                  className="mt-4 w-full rounded-xl bg-[#EFE7D2] border border-[#E6D7A2] px-4 py-3 text-[15px] text-[#1B1D21] outline-none focus:ring-2 focus:ring-[#e0564e] resize-none"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DayBuilder;
